// ============================================================
// STORY MODE — chapter scripts
// ============================================================
//
// The "Maroon5" tutorial chapter. See repo doc CODE-MAP §Story Mode
// for the runtime model; this file is pure data.
//
// Element id reference (matches `constants.ELEM`):
//   1 = Fire (red)    2 = Water (blue)
//   3 = Wind (green)  4 = Light (yellow)   5 = Lightning (purple)
//
// Board layout reference (user-supplied):
//   row 0: [B, B, G, G, B, R]
//   row 1: [R, P, B, P, R, Y]
//   row 2: [R, G, B, Y, P, R]
//   row 3: [G, Y, Y, B, B, Y]
//   row 4: [B, B, G, G, P, P]
//   row 5: [B, B, G, G, P, P]
// ============================================================

import type { ChapterDef, PresetBoard } from './StoryTypes.ts';

const B = 2;  // Water  (blue)
const G = 3;  // Wind   (green)
const R = 1;  // Fire   (red)
const P = 5;  // Lightning (purple)
const Y = 4;  // Light  (yellow)

/** Fixed tutorial board — all three forced swaps happen on this layout. */
const TUTORIAL_BOARD: PresetBoard = {
  grid: [
    [B, B, G, G, B, R],
    [R, P, B, P, R, Y],
    [R, G, B, Y, P, R],
    [G, Y, Y, B, B, Y],
    [B, B, G, G, P, P],
    [B, B, G, G, P, P],
  ],
};

export const MAROON5: ChapterDef = {
  id: 'maroon5',
  title: 'Maroon5',
  subtitle: '初戰魔家四將',
  unlockedByDefault: true,

  conditionalTips: [
    {
      id: 'tip-archer-grass',
      kind: 'archerOnGrass',
      lines: [
        { speaker: 'jiang', text: '弓兵射草中的目標——傷害減半。草叢會遮蔽視線，要打草中敵人，得讓步兵或騎兵先進去逼出來。' },
      ],
    },
    {
      id: 'tip-lightning-puddle',
      kind: 'lightningOnPuddle',
      lines: [
        { speaker: 'jiang', text: '雷電擊中積水上的單位——水能導電，傷害大幅提升。善用此法可建奇功。' },
      ],
    },
    {
      id: 'tip-fire-grass',
      kind: 'fireIgnitesGrass',
      lines: [
        { speaker: 'jiang', text: '火焰點燃了草地！燃燒的草地會持續灼傷站立其上的單位，是極佳的範圍壓制。' },
      ],
    },
    {
      id: 'tip-cavalry-puddle',
      kind: 'cavalryInPuddle',
      lines: [
        { speaker: 'nezha', text: '糟糕，馬蹄陷在積水裡了，本回合只能走一格！騎兵盡量繞開水窪。' },
      ],
    },
    {
      id: 'tip-contest-retreat',
      kind: 'contestRetreat',
      lines: [
        { speaker: 'jiang', text: '兩軍狹路相逢、爭奪同格，血量較少者會被迫退回前一格。預判對手走位是兵家要訣。' },
      ],
    },
  ],

  script: [
    // ============================================================
    // 第一部分：戰前劇情
    // ============================================================
    {
      kind: 'dialogue',
      lines: [
        { speaker: 'narrator', text: '西岐城外，秋風蕭瑟。商兵旗幟連綿至天際，魔家四將陣前耀武揚威。' },
        { speaker: 'jiang', text: '商兵壓城，魔家四將鋒芒畢露。聽聞他們手執法寶，昨日我軍折損不少。' },
        { speaker: 'jiang', text: '今日老夫親自督陣，要會他們一會。哪吒！' },
        { speaker: 'nezha', text: '弟子在！' },
        { speaker: 'jiang', text: '你隨老夫同督陣。前線靠士兵血戰，我們在後方以法術助陣。敵將若亮法寶，切莫硬拚。' },
        { speaker: 'nezha', text: '丞相放心！我的乾坤圈、混天綾擲下去，照樣震他個半天！走，咱們這就上！' },
      ],
    },

    // ============================================================
    // 第二部分 - 1：佈陣教學（互動式）
    // ============================================================
    {
      kind: 'presetDeployment',
      player: [
        // 步兵 ×2 與 弓兵 ×2 —— 已就位，路徑已畫
        { type: 'Infantry', pos: { x: 2, y: 7 }, path: [
          { x: 2, y: 6 }, { x: 2, y: 5 }, { x: 2, y: 4 }, { x: 2, y: 3 },
        ] },
        { type: 'Infantry', pos: { x: 5, y: 7 }, path: [
          { x: 5, y: 6 }, { x: 5, y: 5 }, { x: 5, y: 4 }, { x: 5, y: 3 },
        ] },
        { type: 'Archer', pos: { x: 1, y: 6 }, path: [
          { x: 1, y: 5 }, { x: 1, y: 4 },
        ] },
        { type: 'Archer', pos: { x: 6, y: 6 }, path: [
          { x: 6, y: 5 }, { x: 6, y: 4 },
        ] },
        // 騎兵尚未上陣，等玩家自行放置（見 expectPlace step）
      ],
      ai: [
        // 魔禮紅 = Cavalry，敵方先鋒
        { type: 'Cavalry', pos: { x: 4, y: 1 }, path: [
          { x: 4, y: 2 }, { x: 4, y: 3 }, { x: 4, y: 4 }, { x: 4, y: 5 },
        ] },
        { type: 'Infantry', pos: { x: 2, y: 0 }, path: [
          { x: 2, y: 1 }, { x: 2, y: 2 }, { x: 2, y: 3 },
        ] },
        { type: 'Infantry', pos: { x: 5, y: 0 }, path: [
          { x: 5, y: 1 }, { x: 5, y: 2 }, { x: 5, y: 3 },
        ] },
        { type: 'Archer', pos: { x: 1, y: 1 }, path: [
          { x: 1, y: 2 }, { x: 1, y: 3 },
        ] },
        { type: 'Archer', pos: { x: 6, y: 1 }, path: [
          { x: 6, y: 2 }, { x: 6, y: 3 },
        ] },
      ],
    },
    {
      kind: 'expectPlace',
      type: 'Cavalry',
      hintPos: { x: 3, y: 6 },
      hint: '放這裡',
      lines: [
        { speaker: 'jiang', text: '我軍此戰帶兩名步兵、兩名弓兵、一名騎兵。步兵與弓兵已在兩翼布好，連路徑都規畫了。' },
        { speaker: 'jiang', text: '騎兵還沒派遣——把調色盤上的騎兵拖到高亮的格子。' },
      ],
      after: [
        { speaker: 'jiang', text: '正合適。' },
      ],
    },
    {
      kind: 'expectPathToggle',
      lines: [
        { speaker: 'jiang', text: '要為這名騎兵畫進攻路線，先按下方左側的「Draw Paths」切換到路徑繪製模式。' },
      ],
    },
    {
      kind: 'expectPath',
      unitPos: { x: 3, y: 6 },
      cells: [
        { x: 3, y: 5 }, { x: 3, y: 4 }, { x: 3, y: 3 }, { x: 3, y: 2 }, { x: 3, y: 1 },
      ],
      hint: '依序點擊',
      lines: [
        { speaker: 'jiang', text: '騎兵速度最快，是先鋒。依高亮順序點擊，畫出他衝向敵方基地的路線。' },
      ],
      after: [
        { speaker: 'jiang', text: '好線——直插中軍。' },
      ],
    },
    {
      kind: 'expectStartBattle',
      lines: [
        { speaker: 'jiang', text: '佈陣完成。按右上的「Start Battle」開戰！' },
      ],
    },

    // ============================================================
    // 第二部分 - 2：三消教學（固定版面 3 動）
    //
    // presetBoard 排在 intro dialogue 之前 —— 玩家按 Start Battle 後，
    // _startRound 進入 MATCH3 時就會 consume 這個 preset，把教學版面
    // 載到 board 上。隨後 intro dialogue 才彈出，玩家看到的背景已經
    // 是教學版面（而不是隨機板面）。
    // ============================================================
    {
      kind: 'presetBoard',
      board: TUTORIAL_BOARD,
    },
    {
      kind: 'dialogue',
      lines: [
        { speaker: 'jiang', text: '戰鬥開始。下方寶石板，三顆同色相連即可消除，化為各屬性的魔法積分。' },
        { speaker: 'jiang', text: '先示範第一步——把高亮的紅寶石和它右側的黃寶石互換，造出三紅相連。' },
      ],
    },
    {
      // (1,4)R ↔ (1,5)Y → vertical 3-red at col 5 (rows 0/1/2).
      kind: 'expectSwap',
      r1: 1, c1: 4, r2: 1, c2: 5,
      hint: '紅 ↔ 黃',
      lines: [
        { speaker: 'jiang', text: '把高亮的紅寶石與右邊的黃寶石互換——你會看到右上角立刻形成三紅相連。' },
      ],
      after: [
        { speaker: 'jiang', text: '好！三紅消除，獲得火焰積分。連續消除可以提升法術等級。' },
      ],
    },
    {
      // (0,4)B ↔ (0,3)G — no match formed; teaches "non-matching swaps are allowed".
      kind: 'expectSwap',
      r1: 0, c1: 4, r2: 0, c2: 3,
      hint: '藍向左移',
      noMatchExpected: true,
      lines: [
        { speaker: 'jiang', text: '下一步：把高亮的藍寶石往左移一格。' },
      ],
      after: [
        { speaker: 'jiang', text: '注意——這一手沒有消除任何寶石，但移動本身仍然算數。有時為了下一步佈局，這種「準備手」是必要的。' },
      ],
    },
    {
      // (0,3)B ↔ (0,2)G — creates 5-connected L-shape blue match.
      kind: 'expectSwap',
      r1: 0, c1: 3, r2: 0, c2: 2,
      hint: '藍再向左',
      lines: [
        { speaker: 'jiang', text: '接著把同一顆藍寶石再往左移一格——這次會引爆五顆相連的消除。' },
      ],
      after: [
        { speaker: 'jiang', text: '漂亮！五顆連消的積分遠勝三顆，一口氣拉到 Lv3 治癒。' },
        { speaker: 'jiang', text: '另外，若消除後上方寶石掉落、再形成新的組合，就是「連鎖消除」，每段連鎖都會疊加倍率。' },
        { speaker: 'jiang', text: '五元素對應：紅＝火球（傷害＋點燃草地）、藍＝治療、綠＝護盾、黃＝加攻、紫＝雷擊（積水加成）。' },
        { speaker: 'jiang', text: '剩下的 7 動由你自由發揮——多儲一些積分備戰。' },
      ],
    },

    // ============================================================
    // 第二部分 - 3：魔法部屬教學（待 MATCH3 結束後才接著演）
    // ============================================================
    { kind: 'waitForPhase', phase: 'sched' },
    {
      kind: 'dialogue',
      lines: [
        { speaker: 'jiang', text: '十動已盡，進入法術部屬階段。我們有三個 tick 槽位，把剛剛積累的法術投到戰場關鍵點上。' },
      ],
    },
    {
      // Slot 0: Fire at (4,3) — where the enemy cavalry (魔禮紅) ends tick 0
      // movement (path starts at (4,1), speed 2 → moves to (4,2) → (4,3)).
      kind: 'expectSpell',
      slot: 0,
      el: 1,                          // Fire
      // Target the AI left archer's tick 1 end position. The enemy
      // cavalry will already be in melee with our cavalry by spell
      // resolution time (Step D, after combat), so targeting (4,3) is
      // wasted. Left archer (1,1)→(1,2) is alive + out of combat.
      targetPos: { x: 1, y: 2 },
      hint: '敵左翼弓兵',
      lines: [
        { speaker: 'jiang', text: 'Tick 1：請選火焰魔法，目標是高亮的格子——那是敵方左翼弓兵 tick 1 結束時的位置。先燒掉一個遠程威脅，我方後續推進就輕鬆許多。' },
      ],
      after: [
        { speaker: 'jiang', text: '火焰已布。' },
      ],
    },
    {
      // Slot 1: Water at (6,4) — right archer's tick 2 end position.
      kind: 'expectSpell',
      slot: 1,
      el: 2,                          // Water = Heal
      targetPos: { x: 6, y: 4 },
      hint: '右弓兵 tick2 位置',
      lines: [
        { speaker: 'jiang', text: 'Tick 2：選治癒魔法，目標是高亮的 (6,4)——那是右弓兵 tick 2 結束時的位置。' },
        { speaker: 'jiang', text: '預判走位是部屬階段的核心。觀察一開始畫好的虛線路徑，就能算出單位每個 tick 在哪一格。' },
      ],
      after: [
        { speaker: 'jiang', text: '治癒已布。' },
      ],
    },
    {
      kind: 'expectAnySpell',
      slot: 2,
      lines: [
        { speaker: 'jiang', text: 'Tick 3：自由發揮，挑一個你判斷最有效的法術與目標。' },
      ],
      after: [
        { speaker: 'jiang', text: '排程完畢。' },
      ],
    },
    {
      kind: 'expectConfirmSched',
      lines: [
        { speaker: 'jiang', text: '按右下「✓ Confirm Plan」確認，開始演算！' },
      ],
    },

    // ============================================================
    // 戰鬥演算（暫停式教學）
    // 在 EXEC 期間，chapter.execPauses 會於指定 tick 子階段前彈出對話。
    // ============================================================
    {
      kind: 'dialogue',
      lines: [
        { speaker: 'jiang', text: '接下來會連續演算三個 tick。每個 tick 內依序為：輔助魔法 → 士兵移動 → 攻擊 → 傷害魔法 → 爭奪結算。' },
        { speaker: 'jiang', text: '前兩個 tick 我會在每個階段暫停做提示，第三個 tick 開始你就獨立判斷。' },
      ],
    },
    // 等 EXEC 結束、回到 MATCH3（= round 2）再彈最後一句說明，避免在 tick 動畫中插話。
    { kind: 'waitForPhase', phase: 'match3' },
    {
      kind: 'dialogue',
      lines: [
        { speaker: 'jiang', text: '前三個 tick 結束。接下來戰局走向就交給你——隨機應變，奪下這一勝！' },
      ],
    },
    { kind: 'freePlay' },
  ],

  // ============================================================
  // EXEC 暫停（tick 1 & 2 子階段教學）
  //
  // 攻擊順序教學排在 tick 1 戰鬥前 —— 兩邊騎兵移動後在 (3,4)/(4,3)
  // 對拼（Chebyshev=1，互在範圍內），剛好是同類同時的範例。
  // ============================================================
  execPauses: [
    // Tick 0（= 第 1 回合）輔助魔法階段：本回合沒有 buff/heal 施放，
    // friendly spell event 可能根本不會 emit —— 改在 move 前提示。
    {
      beforeTick: 0,
      beforeType: 'move',
      lines: [
        { speaker: 'jiang', text: 'Tick 1 開始——先結算輔助魔法（治癒／加攻／護盾）。本回合沒有輔助，下一步是士兵移動。' },
      ],
    },
    // Tick 0 戰鬥前：兩邊騎兵狹路相逢 —— 教攻擊順序
    {
      beforeTick: 0,
      beforeType: 'combat',
      lines: [
        { speaker: 'jiang', text: '兩軍前鋒已交鋒！攻擊結算前，先記住一個鐵律——' },
        { speaker: 'jiang', text: '士兵攻擊順序固定：弓兵 → 騎兵 → 步兵，同類型同時行動。' },
        { speaker: 'jiang', text: '所以雙方騎兵會同時對打——不會有先手後手之分；若我方步兵對到敵方弓兵，弓兵先出手。' },
      ],
    },
    // Tick 0 傷害魔法前
    {
      beforeTick: 0,
      beforeType: 'spell',
      beforePhase: 'hostile',
      lines: [
        { speaker: 'jiang', text: '騎兵交鋒結束。接下來是傷害魔法階段——對方主將開始施法。' },
        { speaker: 'narrator', text: '商營魔法蓄勢待發……' },
      ],
    },
    // Tick 1（= 第 2 回合）輔助魔法階段：你部署的治癒會生效
    {
      beforeTick: 1,
      beforeType: 'spell',
      beforePhase: 'friendly',
      lines: [
        { speaker: 'jiang', text: 'Tick 2 開始——你在 Tick 2 部署的治癒要生效了。注意看右翼弓兵的血條立刻補滿。' },
      ],
    },
  ],

  // ============================================================
  // AI 排程（固定，配合教學）
  // 第 1 回合：AI 在 Tick 1 對我方右弓兵 tick1 結束位置 (6,5) 施放火焰，
  // 讓 Tick 2 的治癒能派上用場 (右弓兵 Tick 2 在 (6,4))。
  // Tick 2、3 AI 不施法（Pass），避免複雜化教學。
  // ============================================================
  aiSchedules: {
    1: [
      { slotIndex: 0, spellType: 1, powerLevel: 3, targetPos: { x: 6, y: 5 } },
      { slotIndex: 1, spellType: 0, powerLevel: 0 },
      { slotIndex: 2, spellType: 0, powerLevel: 0 },
    ],
  },

  // ============================================================
  // 第三部分：戰後劇情（勝利）
  // ============================================================
  postBattleScript: [
    {
      kind: 'dialogue',
      lines: [
        { speaker: 'narrator', text: '我軍前鋒擊潰商兵防線，正待乘勝追擊。' },
        { speaker: 'narrator', text: '魔禮紅自陣後緩步走出，拍著掌中那把鑲珠寶的大傘，仰天大笑。' },
        { speaker: 'moliHong', text: '西岐匹夫，休得猖狂！看我法寶！' },
        { speaker: 'narrator', text: '他撐開混元傘，用力一轉。' },
        { speaker: 'narrator', text: '天昏地暗之間，傘下湧出強大吸力。哪吒擲出的乾坤圈、姜子牙手中的打神鞭，全都不受控地飛入傘中！' },
        { speaker: 'narrator', text: '西岐軍倉皇撤退。' },
        { speaker: 'nezha', text: '可惡！丞相，我們明明把商兵打得節節敗退，那破傘到底是什麼妖物？竟把我的乾坤圈吸走了！' },
        { speaker: 'jiang', text: '那叫「混元傘」，能遮天蔽日，更有收納萬物的詭異吸力。連老夫的打神鞭，也一時不察，被它收去了。' },
        { speaker: 'nezha', text: '沒了法寶，明日怎麼打？讓我今晚摸進商營，把兵器偷回來！' },
        { speaker: 'jiang', text: '不可魯莽。魔家四將非泛泛之輩，軍營戒備森嚴。今日陣前雖勝，但折了法寶，後患甚大。' },
        { speaker: 'nezha', text: '那該如何是好？' },
        { speaker: 'jiang', text: '萬物相生相剋。既然力敵難破混元傘，便需智取。你先下去休整，老夫焚香靜坐，卜上一卦，看看破局的契機在何方。' },
        { speaker: 'narrator', text: '（章節完——點下方按鈕返回章節選單）' },
      ],
    },
  ],

  loseScript: [
    {
      kind: 'dialogue',
      lines: [
        { speaker: 'narrator', text: '我軍前線潰敗，姜子牙與哪吒被迫撤回西岐城。' },
        { speaker: 'nezha', text: '可惡！魔禮紅那廝果然不簡單，連帶他手下商兵都這麼難纏！' },
        { speaker: 'jiang', text: '莫氣餒。重新部署兵力、調整法術節奏，必能再戰。' },
        { speaker: 'narrator', text: '（點下方按鈕重試本章）' },
      ],
    },
  ],
};

/** All registered chapters. Future chapters append here. */
export const CHAPTERS: ChapterDef[] = [MAROON5];

export function getChapter(id: string): ChapterDef | null {
  return CHAPTERS.find((c) => c.id === id) || null;
}
